import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/apiService';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

const Profile: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [username, setusername] = useState(user?.username || '');
  const [fullName, setfullName] = useState(user?.full_name || '');
  const [oldPassword, setOldPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [language, setLanguage] = useState(user?.language || i18n.language || 'fr');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const handleSave = async () => {
    setError('');
    setSuccess('');
    if (password || confirmPassword) {   // Check if password fields are filled
      if (!oldPassword) {                // Check if old password is provided when changing password
        setError(t('profile.oldPasswordRequired') || '');
        return;
      }
      if (password !== confirmPassword) { // Check if new password and confirm password match
        setError(t('profile.passwordsMismatch') || '');
        return;
      }
    }
    try {
      await apiClient.put('/auth/me/profile', {
        username: username,
        full_name: fullName,
        old_password: oldPassword || undefined,
        new_password: password || undefined,
        language: language,
      });
      // Appliquer le changement de langue de l'interface uniquement après sauvegarde réussie
      i18n.changeLanguage(language);
      setSuccess(t('profile.updateSuccess') || '');
      setPassword('');
      setConfirmPassword('');
      setOldPassword('');
    } catch (e: any) {
      setError(e.response?.data?.detail || t('profile.updateError') || '');
    }
  };

  const handleLanguageChange = (event: SelectChangeEvent) => {
    const newLanguage = event.target.value as string;
    setLanguage(newLanguage);
    // Ne pas changer la langue de l'interface ici : le changement sera effectif
    // uniquement après avoir cliqué sur "Enregistrer les modifications"
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <Box sx={{ p: 3, maxWidth: 500, mx: 'auto' }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>{t('profile.title')}</Typography>
        {success && <Alert severity="success">{success}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              label={t('profile.username')}
              value={username}
              onChange={e => setusername(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t('profile.email')}
              value={user?.email || ''}
              fullWidth
              InputProps={{
                readOnly: true,
              }}
              variant="outlined"
              sx={{
                '& .MuiInputBase-input': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  cursor: 'default'
                }
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t('profile.fullName')}
              value={fullName}
              onChange={e => setfullName(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="language-select-label">
                {t('profile.interfaceLanguage')}
              </InputLabel>
              <Select
                labelId="language-select-label"
                id="language-select"
                value={language}
                label={t('profile.interfaceLanguage')}
                onChange={handleLanguageChange}
              >
                <MenuItem value="fr">{t('settings.french')}</MenuItem>
                <MenuItem value="en">{t('settings.english')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t('profile.oldPassword')}
              type="password"
              value={oldPassword}
              onChange={e => setOldPassword(e.target.value)}
              fullWidth
              autoComplete="current-password"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t('profile.newPassword')}
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              fullWidth
              autoComplete="new-password"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t('profile.confirmPassword')}
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              fullWidth
              autoComplete="new-password"
            />
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" color="primary" onClick={handleSave} fullWidth>
              {t('profile.save')}
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Button variant="outlined" color="error" onClick={handleLogout} fullWidth>
              {t('profile.logout')}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default Profile;
